import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { YearData, PeriodData, CurrentFiscalContext } from '../../api/periodWeeksApi';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MarketItem {
	id: string;
	displayText: string;
}

export interface RegionMarketEntry {
	markets: MarketItem[];
	loading: boolean;
	error: string | null;
	/** Timestamp of last successful fetch (used for cache validation) */
	fetchedAt: number | null;
}

/** Period-weeks state managed via Redux */
export interface PeriodWeeksState {
	/** Raw API response data keyed by year for quick lookup */
	yearData: YearData[];
	/** Whether the period-weeks API call is in progress */
	loading: boolean;
	/** Error message if the fetch failed */
	error: string | null;
	/** Current fiscal context from the API (today's year/period/week/quarter) */
	currentFiscalContext: CurrentFiscalContext | null;
}

interface AnalyticsHubState {
	/** Currently selected region IDs */
	selectedRegions: string[];
	/** Per-region market data, keyed by region name */
	regionMarkets: Record<string, RegionMarketEntry>;
	/** Combined (merged) market list from all selected regions */
	combinedMarkets: MarketItem[];
	/** Period-weeks cascading filter data */
	periodWeeks: PeriodWeeksState;
}

const initialState: AnalyticsHubState = {
	selectedRegions: [],
	regionMarkets: {},
	combinedMarkets: [],
	periodWeeks: {
		yearData: [],
		loading: false,
		error: null,
		currentFiscalContext: null,
	},
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * De-duplicate markets by id and rebuild combinedMarkets from currently selected regions.
 */
function rebuildCombinedMarkets(state: AnalyticsHubState): void {
	const seen = new Set<string>();
	const combined: MarketItem[] = [];

	for (const regionId of state.selectedRegions) {
		const entry = state.regionMarkets[regionId];
		if (!entry || !entry.markets) continue;
		for (const market of entry.markets) {
			if (!seen.has(market.id)) {
				seen.add(market.id);
				combined.push(market);
			}
		}
	}

	// Sort alphabetically for consistent UI display
	combined.sort((a, b) => a.displayText.localeCompare(b.displayText));
	state.combinedMarkets = combined;
}

// ─── Slice ────────────────────────────────────────────────────────────────────

const analyticsHubSlice = createSlice({
	name: 'analyticsHub',
	initialState,
	reducers: {
		/** Set the full list of selected regions (called after debounce resolves) */
		setSelectedRegions(state, action: PayloadAction<string[]>) {
			state.selectedRegions = action.payload;
			rebuildCombinedMarkets(state);
		},

		/** Mark a region as loading */
		setRegionLoading(state, action: PayloadAction<{ region: string }>) {
			const { region } = action.payload;
			if (!state.regionMarkets[region]) {
				state.regionMarkets[region] = { markets: [], loading: true, error: null, fetchedAt: null };
			} else {
				state.regionMarkets[region].loading = true;
				state.regionMarkets[region].error = null;
			}
		},

		/** Store fetched markets for a region */
		setRegionMarkets(state, action: PayloadAction<{ region: string; markets: MarketItem[] }>) {
			const { region, markets } = action.payload;
			state.regionMarkets[region] = {
				markets,
				loading: false,
				error: null,
				fetchedAt: Date.now(),
			};
			rebuildCombinedMarkets(state);
		},

		/** Mark a region fetch as failed */
		setRegionError(state, action: PayloadAction<{ region: string; error: string }>) {
			const { region, error } = action.payload;
			if (!state.regionMarkets[region]) {
				state.regionMarkets[region] = { markets: [], loading: false, error, fetchedAt: null };
			} else {
				state.regionMarkets[region].loading = false;
				state.regionMarkets[region].error = error;
			}
		},

		/** Remove cached data for a region (e.g. when request was cancelled) */
		clearRegionData(state, action: PayloadAction<{ region: string }>) {
			delete state.regionMarkets[action.payload.region];
			rebuildCombinedMarkets(state);
		},

		/** Full reset of region-market state */
		resetRegionMarkets(state) {
			state.selectedRegions = [];
			state.regionMarkets = {};
			state.combinedMarkets = [];
		},

		// ─── Period-Weeks reducers ────────────────────────────────────────────────

		/** Mark period-weeks fetch as loading */
		setPeriodWeeksLoading(state) {
			state.periodWeeks.loading = true;
			state.periodWeeks.error = null;
		},

		/** Store fetched period-weeks data */
		setPeriodWeeksData(state, action: PayloadAction<YearData[]>) {
			state.periodWeeks.yearData = action.payload;
			state.periodWeeks.loading = false;
			state.periodWeeks.error = null;
		},

		/** Store the current fiscal context from the API response */
		setCurrentFiscalContext(state, action: PayloadAction<CurrentFiscalContext | null>) {
			state.periodWeeks.currentFiscalContext = action.payload;
		},

		/** Mark period-weeks fetch as failed */
		setPeriodWeeksError(state, action: PayloadAction<string>) {
			state.periodWeeks.loading = false;
			state.periodWeeks.error = action.payload;
		},

		/** Reset period-weeks state */
		resetPeriodWeeks(state) {
			state.periodWeeks = { yearData: [], loading: false, error: null, currentFiscalContext: null };
		},
	},
});

export const {
	setSelectedRegions,
	setRegionLoading,
	setRegionMarkets,
	setRegionError,
	clearRegionData,
	resetRegionMarkets,
	setPeriodWeeksLoading,
	setPeriodWeeksData,
	setCurrentFiscalContext,
	setPeriodWeeksError,
	resetPeriodWeeks,
} = analyticsHubSlice.actions;

export default analyticsHubSlice.reducer;
